/**
 * The waiting-words card of the text screen (handoff §16.2; ADR-115 Amendment 2).
 *
 * Card words the daily text-import budget could not take at save wait here, and
 * become cards only when the learner taps after the budget refreshes: nothing
 * happens by itself (the pull model, ADR-063 and ADR-078).
 *
 * Every count and time is the server's. The words and their refresh time are the
 * text's own waiting rows; the budget left is `GET /texts/budget`; the tap is the
 * existing add action, whose row lock makes a repeated or concurrent tap make no card
 * twice. The one thing decided here is when to stop calling the button «before the
 * refresh»: the screen flips at the server's refresh time, and a tap that gets there
 * early is still refused by the server (409), which this card then reports.
 */
import { useEffect, useState } from 'react'
import { Clock } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useLocale } from '../../i18n/context'
import { useAddWaitingWords, useTextBudget } from '../../hooks/useTexts'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../../utils/controlSurface'
import { apiErrorCode, apiErrorDetail } from '../../utils/textFlowErrors'
import { parseUtc } from '../../utils/textFlow'
import type { TextWaitingWord } from '../../types'
import { useRefreshWhen } from './useRefreshWhen'
import { SecondaryPill, WordChip } from './ui'

// setTimeout refuses delays past 2^31 − 1 ms (24.8 days); a refresh is a day away at most.
const MAX_TIMEOUT_MS = 2 ** 31 - 1

interface Props {
  textId: string
  /** The text's waiting words, in text order. */
  words: TextWaitingWord[]
  /** The linked decks, to name the one the cards go to. */
  decks: { id: string; title: string }[]
  /** The generation run the add started, for the screen's status line. */
  onRun: (textImportId: string) => void
}

export default function TextWaitingCard({ textId, words, decks, onRun }: Props) {
  const { tr } = useLocale()
  const ts = tr.textScreen
  const when = useRefreshWhen()
  const add = useAddWaitingWords(textId)

  // The earliest refresh any word waits for: what the server checks the tap against.
  const refreshIso = words.reduce((min, w) => (w.wait_until < min ? w.wait_until : min), words[0].wait_until)
  const refreshMs = parseUtc(refreshIso).getTime()
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const left = refreshMs - Date.now()
    if (left <= 0) return
    const handle = window.setTimeout(() => setNow(Date.now()), Math.min(left + 250, MAX_TIMEOUT_MS))
    return () => window.clearTimeout(handle)
  }, [refreshMs])
  const refreshed = now >= refreshMs

  // Only once the refresh has passed does the budget say anything about the words.
  const budget = useTextBudget(refreshed)
  const k = words.length
  const left = refreshed ? (budget.data?.remaining ?? null) : null
  const exhausted = refreshed && left === 0
  // Until the server has said how much is left, the button does not promise a number.
  const loadingBudget = refreshed && budget.isPending
  const deckTitle = decks.find((d) => d.id === words[0].deck_id)?.title ?? ''

  // The next time the words can be added: the words' own, or the budget's after it refreshed and ran out.
  const nextIso = exhausted && budget.data ? budget.data.refresh_at : refreshIso
  const open = refreshed && !exhausted
  const canAdd = open && !loadingBudget

  let label: string
  if (add.isPending) label = ts.adding
  else if (!open) label = ts.addLater(when(nextIso))
  else if (!loadingBudget && left !== null && left > 0 && left < k) label = ts.addSome(left, k)
  else label = ts.addAll(k, deckTitle)

  function tap() {
    add.mutate(undefined, {
      onSuccess: (result) => {
        if (result.cards_accepted > 0) toast.success(ts.added(result.cards_accepted, deckTitle))
        if (result.text_import_id) onRun(result.text_import_id)
      },
      onError: (err) => {
        if (apiErrorCode(err) === 'TEXT_WAITING_NOT_YET') {
          const at = apiErrorDetail(err)?.refresh_at
          toast.error(ts.notYet(when(typeof at === 'string' ? at : refreshIso)))
        } else {
          toast.error(ts.addFailed)
        }
      },
    })
  }

  return (
    <section
      data-testid="text-waiting-card"
      className={CONTROL_SURFACE}
      onContextMenu={suppressTouchContextMenu}
      aria-label={tr.textList.waiting(k)}
      style={{
        padding: '14px 16px', borderRadius: 16, background: 'var(--bg-surface)',
        border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10,
        fontFamily: 'var(--font-ui)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Clock size={18} aria-hidden style={{ color: 'var(--accent)', flexShrink: 0 }} />
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{tr.textList.waiting(k)}</h2>
      </div>
      <p data-testid="text-waiting-body" style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'var(--text-secondary)' }}>
        {open ? ts.waitingAfter : ts.waitingBefore(when(nextIso))}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {words.map((w) => <WordChip key={w.id}>{w.word}</WordChip>)}
      </div>
      <SecondaryPill
        full
        height={44}
        data-testid="text-waiting-add"
        disabled={!canAdd || add.isPending}
        onClick={tap}
        style={canAdd && !add.isPending ? undefined : { color: 'var(--text-tertiary)', cursor: 'default' }}
      >
        {label}
      </SecondaryPill>
    </section>
  )
}
